import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { ResponseItemView } from '@app/src/context/ResponseItemContextProvider'
import type { AiSuggestion } from '@app/src/pages/Questionnaire/hooks/useFetchFileAiSuggestions'
import NumberQuestionFieldWithDeviation from '@app/src/pages/Questionnaire/Provider/Report/NumberQuestionFieldWithDeviation'
import TextQuestionInputField from '@app/src/pages/Questionnaire/Provider/Report/TextQuestionInputField'
import type { RequestItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import { Box } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import QuestionSuggestionFooter from './SuggestedAnswers/QuestionSuggestionFooter'

type TextFieldWithSuggestionsProps = {
  requestItem: RequestItem
  disabled?: boolean
  hasError?: boolean
  error?: string
  isPreview: boolean
}

const getFieldValueFromSuggestion = (value: string, questionType: string) => {
  if (questionType?.toLowerCase() !== QuestionTypes.Number.toLowerCase()) return value

  const match = value.match(/\d+(?:[.,]\d+)?/)
  if (!match) return undefined
  return parseFloat(match[0]?.replace(',', '.'))
}

const TextFieldWithSuggestions: React.FC<TextFieldWithSuggestionsProps> = ({
  requestItem,
  disabled,
  hasError,
  error,
  isPreview,
}) => {
  const { setValue, trigger } = useFormContext()
  const [hasAppliedSuggestion, setHasAppliedSuggestion] = useState(false)
  const { hideInputField, setResponseItemView, previousAnswer, setQuestionSkippedFields } = useResponseItemContext()
  const [appliedValue, setAppliedValue] = useState<string>()

  const name = `${requestItem.id}.answer`
  const valueWatch = useWatch({ name })

  const questionType = requestItem.questionType?.name.toLowerCase()

  useEffect(() => {
    if (!appliedValue || appliedValue === valueWatch?.toString()) return
    setAppliedValue(undefined)
    setResponseItemView(prevState =>
      prevState === ResponseItemView.SuggestedAnswerAccepted ? ResponseItemView.ShowSuggestedAnswer : prevState,
    )
  }, [valueWatch])

  const applyValueFromString = (value: string) => {
    const valueToSet = getFieldValueFromSuggestion(value, questionType)
    setValue(name, valueToSet)
    setAppliedValue(valueToSet?.toString())
  }

  const afterSuggestionApplied = async () => {
    setHasAppliedSuggestion(true)
    setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)
    await trigger()
  }

  const onApplyAiSuggestion = async (suggestion: AiSuggestion) => {
    if (suggestion.cannotAnswer) {
      setQuestionSkippedFields(AnswerStatus.NotAvailable)
    } else applyValueFromString(suggestion.response)

    await afterSuggestionApplied()
  }

  const onApplyPreviousAnswer = async (value: string) => {
    if (!previousAnswer) return

    if (previousAnswer.cannotAnswer) setQuestionSkippedFields()
    else applyValueFromString(value)

    await afterSuggestionApplied()
  }

  const onIgnoreSuggestion = () => {
    setValue(name, undefined)
    setValue(`${requestItem.id}.cannotAnswer`, false)
    setValue(`${requestItem.id}.answerStatus`, AnswerStatus.Answered)
    setAppliedValue(undefined)
    setResponseItemView(ResponseItemView.ShowSuggestedAnswer)
  }

  return (
    <>
      <Box display={hideInputField ? 'none' : undefined} px={4} pb={isPreview ? 0 : 2}>
        {questionType?.toLowerCase() === QuestionTypes.Number.toLowerCase() ? (
          <NumberQuestionFieldWithDeviation
            name={name}
            requestItem={requestItem}
            hasError={hasError}
            error={error}
            disabled={disabled}
            hasAppliedSuggestion={hasAppliedSuggestion}
          />
        ) : (
          <TextQuestionInputField
            name={name}
            requestItem={requestItem}
            hasError={hasError}
            error={error}
            disabled={disabled}
            hasAppliedSuggestion={hasAppliedSuggestion}
          />
        )}
      </Box>
      <QuestionSuggestionFooter
        requestItem={requestItem}
        questionType={questionType}
        onApplyPreviousAnswer={async () => await onApplyPreviousAnswer(previousAnswer?.answer?.toString() ?? '')}
        onApplyAiSuggestion={onApplyAiSuggestion}
        onIgnoreSuggestion={onIgnoreSuggestion}
        isPreview={isPreview}
      />
    </>
  )
}

export default TextFieldWithSuggestions
